import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  updateAccountInformationSchema,
  updateAccountEmailSchema,
} from 'utils/form-validation-schema';
import { getStates, updateAccountInformation } from 'services/UserAccountService';
import { Callout } from 'components/UI/Callout/Callout';
import { shapeUpdateAccountPayload } from './account-information-form-utils';
import { ContactFormSection } from './ContactFormSection';
import { AddressFormSection } from './AddressFormSection';

export const AccountInformation = (props) => {
  const {
    Account,
    AddressModel: {
      State,
      StateDescription,
      CountryDescription,
      Street1,
      Street2,
      Suburb,
      Postcode,
    },
    DateOfBirth,
    States,
    Countries,
    Prefixes,
  } = props;
  const [statesObject, setStatesObject] = useState(States);
  const {
    Email,
    FirstName,
    LastName,
    PhoneNumber,
    MobileNumber,
    DOBDay,
    DOBMonth,
    DOBYear,
    Title,
  } = Account;

  const [selectedPrefix, setSelectedPrefix] = useState(Title);
  const [selectedDobDay, setSelectedDobDay] = useState(DOBDay);
  const [selectedDobMonth, setSelectedDobMonth] = useState(DOBMonth);
  const [selectedDobYear, setSelectedDobYear] = useState(DOBYear);
  const [selectedState, setSelectedState] = useState(State);
  const [selectedCountry, setSelectedCountry] = useState(CountryDescription);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [updateEmail, setUpdateEmail] = useState(false);
  const [selectedStateDescription, setSelectedStateDescription] = useState(StateDescription);
  const [requestChange, setRequestChange] = useState(false);
  const [retainPasswordFocus, setRetainPasswordFocus] = useState(false);

  const inputsDefaultValues = {
    regInputEmail: Email,
    regFirstName: FirstName,
    regLastName: LastName,
    regPhoneNumber: PhoneNumber,
    regMobileNumber: MobileNumber,
    deliveryAddress1: Street1,
    deliveryAddress2: Street2,
    suburb: Suburb,
    postcode: Postcode,
  };

  const methods = useForm({
    criteriaMode: 'all',
    mode: 'onChange',
    resolver: yupResolver(updateEmail ? updateAccountEmailSchema : updateAccountInformationSchema),
    defaultValues: inputsDefaultValues,
  });

  const {
    formState: { isValid, isDirty },
    setValue,
    resetField,
  } = methods;

  /**
   * After Api response, Account object and AddressModel object
   * is being changed from the value of api response. Re-set the
   * inputs values
   */
  const setInputValuesAfterApiResponse = (responseValues) => {
    const { Account: accountObject, AddressModel: addressObject } = responseValues;

    const updatedInputsValues = {
      regInputEmail: accountObject?.Email,
      regFirstName: accountObject?.FirstName,
      regLastName: accountObject?.LastName,
      regPhoneNumber: accountObject?.PhoneNumber,
      regMobileNumber: accountObject?.MobileNumber,
      suburb: addressObject?.Suburb,
      postcode: addressObject?.Postcode,
    };

    Object.keys(updatedInputsValues).forEach((key) => {
      const item = updatedInputsValues[key];

      if (item) {
        setValue(key, updatedInputsValues[key]);
      }
    });
  };

  const saveAccountInformation = async (requestPayload) => {
    setIsProcessing(true);
    const response = await updateAccountInformation(requestPayload);

    setSuccessMessage(response?.SuccessMessage);
    setErrorMessage(response?.ErrorMessage);

    if (response?.SuccessMessage) {
      setInputValuesAfterApiResponse(response);
    } else {
      setSubmitButtonDisabled(true);
    }

    resetField('updateEmailInputPassword');
    setRetainPasswordFocus(true);
    setRequestChange(false);
    setIsProcessing(false);
  };

  const accountFormSubmitHandler = (inputsData) => {
    const requestPayload = shapeUpdateAccountPayload({
      inputsData,
      dropdownsData: {
        selectedDobDay,
        selectedDobMonth,
        selectedDobYear,
        selectedPrefix,
        selectedState,
        selectedCountry,
      },
      changeEmailAddress: updateEmail,
    });

    const stateObj = statesObject.find((state) => state.StateCode === selectedState);
    const stateId = stateObj?.Id || 0;
    const stateDescription = stateObj.Description;
    requestPayload.AddressModel.StateId = stateId;
    requestPayload.AddressModel.StateDescription = stateDescription;

    const countryId = Countries.find((country) => country.Description === selectedCountry).Id;
    requestPayload.AddressModel.CountryId = countryId;

    saveAccountInformation(requestPayload);
  };

  const selectedCountryChangeHandler = async (selectedCountryDesc) => {
    const countryId = Countries.find(
      (countryObj) => countryObj.Description === selectedCountryDesc
    ).Id;

    const response = await getStates(countryId);

    if (response.length) {
      const newStatesArray = response.map((state) => {
        const newStateObj = {
          Country: state.country,
          Description: state.description,
          Inactive: state.inactive,
          Id: state.id,
          StateCode: state.stateCode,
        };

        return newStateObj;
      });

      const defaultStateIndex = 7;
      setStatesObject(newStatesArray);
      setSelectedState(newStatesArray[defaultStateIndex]?.StateCode);
      setSelectedStateDescription(newStatesArray[defaultStateIndex]?.Description);
    } else {
      setStatesObject([{ StateId: 0, StateCode: '', Description: 'N/A' }]);
      setSelectedState('');
      setSelectedStateDescription('N/A');
    }
  };

  const contactFormSectionProps = {
    Account,
    Prefixes,
    DateOfBirth,
    setSelectedPrefix,
    setSelectedDobDay,
    setSelectedDobMonth,
    setSelectedDobYear,
  };

  // Enable Save button when any of the input values changed
  useEffect(() => {
    setSubmitButtonDisabled(!isValid || !isDirty);
  }, [isValid, isDirty]);

  // Enable Save button when any of the dropdown values changed
  useEffect(() => {
    setSubmitButtonDisabled(false);
  }, [
    selectedPrefix,
    selectedDobDay,
    selectedDobMonth,
    selectedDobYear,
    selectedState,
    selectedCountry,
  ]);

  return (
    <section className="container account-information">
      <div className="row">
        <div className="col-md-8 col-md-offset-1">
          <h2>Account information</h2>
          <p className="large form-intro">
            Need help? Contact us on <strong>1300 182 183</strong> between 9am - 4:30pm Monday -
            Friday.
            <br />
            Please note that hours are subject to change on Public Holidays.
          </p>
          <p>All fields required unless otherwise marked.</p>
        </div>
      </div>

      <FormProvider {...methods}>
        <form
          className="form-account"
          name="accountInfoForm"
          noValidate="novalidate"
          onSubmit={methods.handleSubmit(accountFormSubmitHandler)}
        >
          <div className="row">
            <div className="col-sm-6 col-md-4 col-md-offset-1">
              <ContactFormSection
                {...contactFormSectionProps}
                setUpdateEmail={setUpdateEmail}
                requestChange={requestChange}
                setRequestChange={setRequestChange}
                retainPasswordFocus={retainPasswordFocus}
                setRetainPasswordFocus={setRetainPasswordFocus}
              />

              <AddressFormSection
                States={statesObject}
                Countries={Countries}
                selectedState={selectedState}
                setSelectedState={setSelectedState}
                setSelectedCountry={setSelectedCountry}
                selectedCountry={selectedCountry}
                selectedStateDescription={selectedStateDescription}
                selectedCountryChangeHandler={selectedCountryChangeHandler}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-8 col-md-offset-1">
              {errorMessage && <Callout errorMessage={errorMessage} />}

              {successMessage && <Callout successMessage={successMessage} />}
              <br />

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isProcessing || submitButtonDisabled}
              >
                <span>{isProcessing ? 'Processing...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </section>
  );
};

AccountInformation.propTypes = {
  Account: PropTypes.object,
  AddressModel: PropTypes.object,
  DateOfBirth: PropTypes.object,
  States: PropTypes.arrayOf(PropTypes.object),
  Countries: PropTypes.arrayOf(PropTypes.object),
  Prefixes: PropTypes.arrayOf(PropTypes.object),
};

export const AccountInformationSC = (props) => {
  const { sitecoreData } = props;
  return <AccountInformation {...sitecoreData} />;
};

AccountInformationSC.propTypes = {
  sitecoreData: PropTypes.object,
};
